import { HUD } from '../../config/tuning';
import type { HudPainter } from '../HudLayout';
import type { HudDegradation } from '../HudDegradation';
import type { HudFrame } from '../HudFrame';

/**
 * The heading tape (SPEC §3.4.1): a ribbon across the top with 10° graduations.
 *
 * Under GPS jamming it degrades to a drifting magnetic reading rather than disappearing, and is
 * struck through so the player can see at a glance that the number is no longer trustworthy —
 * a wrong heading presented confidently is worse than no heading at all.
 */

const RAD_TO_DEG = 180 / Math.PI;

/** Wrap a heading in degrees to [0, 360). */
export function wrapHeadingDeg(headingDeg: number): number {
  const wrapped = headingDeg % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

/** Shortest signed difference between two headings, degrees, in (−180, 180]. */
export function headingDeltaDeg(fromDeg: number, toDeg: number): number {
  let delta = (toDeg - fromDeg) % 360;
  if (delta > 180) delta -= 360;
  if (delta <= -180) delta += 360;
  return delta;
}

export function drawHeadingTape(
  painter: HudPainter,
  frame: HudFrame,
  degradation: HudDegradation,
): void {
  const layout = painter.layout;
  const ctx = painter.ctx;

  const displayedRad = frame.headingRad + degradation.headingErrorRad;
  const headingDeg = wrapHeadingDeg(displayedRad * RAD_TO_DEG);

  const halfWidth = layout.px(HUD.headingTapeWidthPx) * 0.5;
  const pxPerDeg = 1 / (HUD.headingTapeDegPerPx * layout.scale);
  const spanDeg = halfWidth / pxPerDeg;
  const tick = layout.px(HUD.headingTapeTickPx);
  const y = layout.px(HUD.marginPx);

  ctx.save();
  ctx.translate(layout.centreX, y);

  const first = Math.ceil((headingDeg - spanDeg) / 5) * 5;
  for (let degrees = first; degrees <= headingDeg + spanDeg; degrees += 5) {
    const x = headingDeltaDeg(headingDeg, degrees) * pxPerDeg;
    if (Math.abs(x) > halfWidth) continue;
    const major = wrapHeadingDeg(degrees) % 10 === 0;
    painter.line(x, 0, x, major ? tick : tick * 0.55);
    if (major) {
      painter.text(tapeLabel(wrapHeadingDeg(degrees)), x, tick + layout.fontPx * 1.1, 'centre');
    }
  }

  // Centre caret and the current value.
  ctx.beginPath();
  ctx.moveTo(0, -tick * 0.9);
  ctx.lineTo(-tick * 0.6, -tick * 1.8);
  ctx.lineTo(tick * 0.6, -tick * 1.8);
  ctx.closePath();
  painter.stroke();

  const label = tapeLabel(Math.round(headingDeg));
  const labelY = -tick * 3.1;
  painter.text(label, 0, labelY, 'centre');

  if (degradation.headingUnreliable) {
    const width = painter.measure(label);
    painter.line(-width * 0.62, labelY, width * 0.62, labelY);
  }

  ctx.restore();
}

/** Three-digit heading label, cached — the tape draws up to a dozen of these every frame. */
const TAPE_LABELS = new Map<number, string>();
function tapeLabel(degrees: number): string {
  const key = Math.round(degrees) % 360;
  let label = TAPE_LABELS.get(key);
  if (label === undefined) {
    label = key.toFixed(0).padStart(3, '0');
    TAPE_LABELS.set(key, label);
  }
  return label;
}
